import { useState, useEffect } from "react";
import {
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    CircularProgress,
    Typography,
    Tooltip,
    InputAdornment,
    TableContainer,
    Fade,
    IconButton,
    Drawer,
    useMediaQuery,
    useTheme,
    Collapse,
    Box,
    Chip
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { fetchData } from "../../../apiService";
import { useAuth } from "../../../AuthContext";
import { tokenIsExpired } from "../../../authUtils";

function ItemForecastReport() {
    const { isAuthenticated, companyId, branchId, token, logout, username } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [toDate, setToDate] = useState("");
    const [selectedItemId, setSelectedItemId] = useState('');
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [expandedRows, setExpandedRows] = useState({});

    // Company and Branch details for Excel export
    const [companyDetails, setCompanyDetails] = useState(null);
    const [branchDetails, setBranchDetails] = useState(null);

    useEffect(() => {
        setMounted(true);
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }

        const today = new Date();
        setToDate(today.toISOString().split("T")[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (toDate && companyId && branchId && token) {
            fetchTableData();
            fetchCompanyDetails();
            fetchBranchDetails();
            fetchItemsData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toDate]);

    const fetchCompanyDetails = async () => {
        try {
            const response = await fetchData('CompanyApi', `/${companyId}`, token);
            if (response && response.responseCode === '1111') {
                const data = response.response_data || response.data || response;
                setCompanyDetails(Array.isArray(data) ? data[0] : data);
            }
        } catch (error) {
            console.error('Error fetching company details:', error);
        }
    };

    const fetchBranchDetails = async () => {
        try {
            const response = await fetchData('BranchApi', `/${branchId}`, token);
            if (response && response.responseCode === '1111') {
                const data = response.response_data || response.data || response;
                setBranchDetails(Array.isArray(data) ? data[0] : data);
            }
        } catch (error) {
            console.error('Error fetching branch details:', error);
        }
    };

    const fetchItemsData = async () => {
        try {
            const response = await fetchData('ItemApi', `/${companyId}`, token);
            if (response && response.responseCode === '1111' && response.response_data) {
                setItems(response.response_data);
            } else {
                setItems([]);
            }
        } catch (error) {
            console.error('Error fetching items:', error);
            setItems([]);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-GB");
    };

    const fetchTableData = async () => {
        if (!toDate) {
            return;
        }
        setIsLoading(true);
        
        // Reset filters to show all data
        setSearchQuery("");
        
        try {
            const itemId = selectedItemId || 0; // Use 0 for all items
            const response = await fetchData(
                "ReportApi",
                `/Forecast/${toDate}/${companyId}/${itemId}`,
                token
            );

            if (response.responseCode === "1111") {
                console.log("Item Forecast data:", response.response_data);
                setDataItem(response.response_data || []);
            }
        } catch (error) {
            console.error("Error fetching item forecast:", error);
            setDataItem([]);
        } finally {
            setIsLoading(false);
        }
    };

    const filteredData = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    // Group data by item name
    const groupedData = filteredData.reduce((acc, item) => {
        const itemName = item.Name;
        if (!acc[itemName]) {
            acc[itemName] = {
                itemName: itemName,
                totalStock: item.TotalStock,
                totalOnRent: 0,
                totalForecast: 0,
                instances: []
            };
        }
        acc[itemName].totalOnRent += item.CurrentlyOnRentQty || 0;
        acc[itemName].totalForecast += item.ForecastQty || 0;
        acc[itemName].instances.push(item);
        return acc;
    }, {});

    const groupedArray = Object.values(groupedData);

    const processedData = groupedArray.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const toggleRow = (itemName) => {
        setExpandedRows(prev => ({
            ...prev,
            [itemName]: !prev[itemName]
        }));
    };

    const handleExportExcel = async () => {
        if (filteredData.length === 0) {
            alert("No data to export");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Item Forecast Report");

        // Get company and branch names
        const companyName = companyDetails?.CompanyName || companyDetails?.Name || 'N/A';
        const branchName = branchDetails?.BranchName || branchDetails?.Name || 'N/A';

        // Add Company and Branch on same line
        const companyBranchRow = worksheet.addRow(['Company:', companyName, '|', 'Branch:', branchName]);
        companyBranchRow.font = { bold: true, size: 12 };
        companyBranchRow.getCell(1).font = { bold: true };
        companyBranchRow.getCell(4).font = { bold: true };

        // Add Date Range, Generated By, and Generated Time on same line
        const currentDateTime = new Date().toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
        const infoRow = worksheet.addRow([
            'To Date:', 
            formatDate(toDate),
            '|',
            'Generated By:',
            username || 'N/A',
            '|',
            'Generated Time:',
            currentDateTime
        ]);
        infoRow.font = { bold: true, size: 12 };
        infoRow.getCell(1).font = { bold: true };
        infoRow.getCell(4).font = { bold: true };
        infoRow.getCell(7).font = { bold: true };

        // Add empty row
        worksheet.addRow([]);

        // Add Summary Header
        const summaryHeaderRow = worksheet.addRow([
            "Item Name",
            "Total Stock",
            "Total On Rent",
            "Total Forecast",
            "Bookings Count"
        ]);

        summaryHeaderRow.font = { bold: true };
        summaryHeaderRow.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF667eea" }
        };
        summaryHeaderRow.font = { color: { argb: "FFFFFFFF" }, bold: true };

        // Add grouped summary data
        groupedArray.forEach((group) => {
            const summaryRow = worksheet.addRow([
                group.itemName,
                group.totalStock,
                group.totalOnRent,
                group.totalForecast,
                group.instances.length
            ]);
            summaryRow.font = { bold: true };
            summaryRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFF3F4F6" }
            };

            // Add detail rows if multiple instances
            if (group.instances.length > 1) {
                // Detail header
                const detailHeaderRow = worksheet.addRow([
                    "",
                    "On Rent Qty",
                    "Forecast Qty",
                    "Forecast Date",
                    "Expected Return Date",
                    "Return Time"
                ]);
                detailHeaderRow.font = { bold: true, size: 10 };
                detailHeaderRow.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFE5E7EB" }
                };

                // Detail rows
                group.instances.forEach((instance, idx) => {
                    worksheet.addRow([
                        `  ${idx + 1}.`,
                        instance.CurrentlyOnRentQty || 0,
                        instance.ForecastQty || 0,
                        instance.ForecastDate,
                        instance.ExpectedReturnDate,
                        instance.ExpectedReturnTime || 'N/A'
                    ]);
                });

                // Add spacing row
                worksheet.addRow([]);
            } else {
                // Single instance - add details inline
                const instance = group.instances[0];
                worksheet.addRow([
                    "  Details:",
                    instance.CurrentlyOnRentQty || 0,
                    instance.ForecastQty || 0,
                    instance.ForecastDate,
                    instance.ExpectedReturnDate,
                    instance.ExpectedReturnTime || 'N/A'
                ]);
                worksheet.addRow([]);
            }
        });

        // Set column widths
        worksheet.columns.forEach((col) => {
            col.width = 20;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const fileName = `Item_Forecast_Report_${toDate}.xlsx`;
        saveAs(new Blob([buffer]), fileName);
    };

    if (!mounted) return null;

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{
                minWidth: '100%',
                background: '#f5f7fa',
                padding: '10px',
                position: 'relative'
            }}>
                <style>{`
                    @keyframes slideIn {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                    }
                    .action-button {
                        text-transform: none;
                        font-weight: 600;
                        letter-spacing: 0.5px;
                    }
                `}</style>

                <div style={{
                    width: '100%',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    padding: '12px',
                    position: 'relative',
                    color: 'black',
                    fontFamily: 'Times New Romans',
                }}>
                    {/* Header Section with Title */}
                    <div style={{
                        marginBottom: '12px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <h4 style={{
                            fontSize: '17px',
                            fontWeight: '700',
                            margin: 0,
                            letterSpacing: '-0.3px',
                            fontFamily: 'Times New Romans',
                            textTransform: 'uppercase',
                            color: 'black'
                        }}>
                            Item Forecast Report
                        </h4>
                    </div>

                    {/* Filters and Buttons Row */}
                    {!isMobile ? (
                        // Desktop View
                        <div style={{ 
                            marginBottom: '12px', 
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                gap: '8px', 
                                flexWrap: 'nowrap',
                                minWidth: 'fit-content',
                                alignItems: 'flex-end'
                            }}>
                                <div style={{ flexShrink: 0 }}>
                                    <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px', paddingLeft: '4px' }}>To Date</div>
                                    <TextField
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        size="small"
                                        sx={{
                                            width: '140px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                backgroundColor: '#f8f9fa',
                                                fontSize: '11px',
                                                height: '28px',
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                            },
                                        }}
                                    />
                                </div>

                                <div style={{ flexShrink: 0 }}>
                                    <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px', paddingLeft: '4px' }}>Item (Optional)</div>
                                    <TextField
                                        select
                                        value={selectedItemId}
                                        onChange={(e) => setSelectedItemId(e.target.value)}
                                        size="small"
                                        SelectProps={{ native: true }}
                                        sx={{
                                            width: '300px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                backgroundColor: '#f8f9fa',
                                                fontSize: '11px',
                                                height: '28px',
                                                '&:hover fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#667eea',
                                                },
                                            },
                                        }}
                                    >
                                        <option value="">All Items</option>
                                        {items.map(item => (
                                            <option key={item.Id} value={item.Id}>
                                                {item.Code} - {item.Name}
                                            </option>
                                        ))}
                                    </TextField>
                                </div>
                            
                                <TextField
                                    placeholder="Search items..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    size="small"
                                    sx={{
                                        flexGrow: 1,
                                        minWidth: '200px',
                                        flexShrink: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '28px',
                                            '&:hover fieldset': {
                                                borderColor: '#667eea',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#667eea',
                                            },
                                        },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon style={{ color: '#667eea', fontSize: '14px' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <Button
                                    variant="contained"
                                    onClick={fetchTableData}
                                    disabled={isLoading}
                                    startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon style={{ fontSize: '14px' }} />}
                                    style={{
                                        background: 'linear-gradient(135deg, #11998e 0%, #061f0fff 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 6px rgba(17, 153, 142, 0.3)',
                                        border: 'none',
                                        height: '28px',
                                        textTransform: 'none',
                                        letterSpacing: '0.3px',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0
                                    }}
                                >
                                    {isLoading ? "Loading..." : "Refresh"}
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={handleExportExcel}
                                    disabled={filteredData.length === 0}
                                    startIcon={<FileDownloadIcon style={{ fontSize: '14px' }} />}
                                    style={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 6px rgba(102, 126, 234, 0.3)',
                                        border: 'none',
                                        height: '28px',
                                        textTransform: 'none',
                                        letterSpacing: '0.3px',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0
                                    }}
                                >
                                    Export Excel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        // Mobile View
                        <div style={{ 
                            marginBottom: '12px', 
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                gap: '8px', 
                                alignItems: 'center',
                                minWidth: 'fit-content'
                            }}>
                                <TextField
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    size="small"
                                    sx={{
                                        width: '130px',
                                        flexShrink: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '32px',
                                        },
                                    }}
                                />

                                <Tooltip title="Refresh">
                                    <IconButton
                                        onClick={fetchTableData}
                                        disabled={isLoading}
                                        sx={{
                                            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                            color: 'white',
                                            width: '36px',
                                            height: '32px',
                                            borderRadius: '6px',
                                            flexShrink: 0
                                        }}
                                    >
                                        {isLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Export">
                                    <IconButton
                                        onClick={handleExportExcel}
                                        disabled={filteredData.length === 0}
                                        sx={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            width: '36px',
                                            height: '32px',
                                            borderRadius: '6px',
                                            flexShrink: 0
                                        }}
                                    >
                                        <FileDownloadIcon />
                                    </IconButton>
                                </Tooltip>
                                
                                <Tooltip title="Filter & Search">
                                    <IconButton
                                        onClick={() => setFilterDrawerOpen(true)}
                                        sx={{
                                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                            color: 'white',
                                            width: '36px',
                                            height: '32px',
                                            borderRadius: '6px',
                                            flexShrink: 0
                                        }}
                                    >
                                        <FilterListIcon />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </div>
                    )}

                    {/* Mobile Filter Drawer */}
                    <Drawer
                        anchor="right"
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                    >
                        <div style={{ width: '280px', padding: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <Typography variant="h6" style={{ fontWeight: '600' }}>Search</Typography>
                                <IconButton onClick={() => setFilterDrawerOpen(false)} size="small">
                                    <CloseIcon />
                                </IconButton>
                            </div>

                            <div>
                                <TextField
                                    fullWidth
                                    placeholder="Search items..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon style={{ color: '#667eea' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </div>
                        </div>
                    </Drawer>

                    {/* Table */}
                    <div style={{ marginBottom: '10px' }}>
                        <TableContainer component={Paper} style={{
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                            borderRadius: '10px',
                            overflow: 'hidden'
                        }}>
                            <Table sx={{ minWidth: 650 }} aria-label="item forecast table">
                                <TableHead>
                                    <TableRow style={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    }}>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '4px 8px',
                                            height: '10px',
                                            width: '40px'
                                        }} />
                                        {["Item Name", "Total Stock", "Total On Rent", "Total Forecast", "Date Range"].map((head, idx) => (
                                            <TableCell key={head} align={idx >= 2 && idx <= 4 ? "right" : "left"} style={{
                                                color: 'white',
                                                fontWeight: '600',
                                                fontSize: '11px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                padding: '4px 8px',
                                                height: '10px'
                                            }}>
                                                {head}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                                <CircularProgress size={40} sx={{ color: '#667eea' }} />
                                                <Typography sx={{ mt: 2, color: '#64748b' }}>
                                                    Loading forecast data...
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : processedData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                                <Typography color="text.secondary" style={{ fontSize: '12px' }}>
                                                    No forecast data found for the selected date
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        processedData.map((group, index) => (
                                            <>
                                                {/* Main Row - Grouped Summary */}
                                                <TableRow 
                                                    key={`group-${index}`}
                                                    style={{
                                                        background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                                        borderBottom: expandedRows[group.itemName] ? 'none' : '2px solid #f0f0f0',
                                                        cursor: group.instances.length > 1 ? 'pointer' : 'default'
                                                    }}
                                                    onClick={() => group.instances.length > 1 && toggleRow(group.itemName)}
                                                    hover={group.instances.length > 1}
                                                >
                                                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', width: '40px' }}>
                                                        {group.instances.length > 1 ? (
                                                            <IconButton size="small" sx={{ padding: '2px' }}>
                                                                {expandedRows[group.itemName] ? 
                                                                    <KeyboardArrowUpIcon fontSize="small" /> : 
                                                                    <KeyboardArrowDownIcon fontSize="small" />
                                                                }
                                                            </IconButton>
                                                        ) : null}
                                                    </TableCell>

                                                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '600' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            {group.itemName}
                                                            {group.instances.length > 1 && (
                                                                <Chip 
                                                                    label={`${group.instances.length} bookings`}
                                                                    size="small"
                                                                    sx={{
                                                                        height: '20px',
                                                                        fontSize: '10px',
                                                                        fontWeight: '600',
                                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                                        color: 'white',
                                                                        '& .MuiChip-label': {
                                                                            color: 'white',
                                                                            paddingLeft: '8px',
                                                                            paddingRight: '8px'
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                    </TableCell>

                                                    <TableCell align="right" style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '600' }}>
                                                        {group.totalStock || 0}
                                                    </TableCell>

                                                    <TableCell align="right" style={{ fontSize: '12px', padding: '8px 12px', color: '#f59e0b', fontWeight: '600' }}>
                                                        {group.totalOnRent || 0}
                                                    </TableCell>

                                                    <TableCell align="right" style={{ fontSize: '12px', padding: '8px 12px', color: '#667eea', fontWeight: '600' }}>
                                                        {group.totalForecast || 0}
                                                    </TableCell>

                                                    <TableCell style={{ fontSize: '11px', padding: '8px 12px', color: '#64748b', fontStyle: 'italic' }}>
                                                        {group.instances.length === 1 
                                                            ? `${(group.instances[0].ForecastDate)} → ${(group.instances[0].ExpectedReturnDate)}`
                                                            : `${group.instances.length} different dates`
                                                        }
                                                    </TableCell>
                                                </TableRow>

                                                {/* Expanded Detail Rows */}
                                                {group.instances.length > 1 && (
                                                    <TableRow key={`expand-${index}`}>
                                                        <TableCell 
                                                            colSpan={6} 
                                                            style={{ 
                                                                padding: 0, 
                                                                background: index % 2 === 0 ? '#f8f9fa' : '#ffffff',
                                                                borderBottom: '2px solid #f0f0f0'
                                                            }}
                                                        >
                                                            <Collapse in={expandedRows[group.itemName]} timeout="auto" unmountOnExit>
                                                                <Box sx={{ margin: 2, marginLeft: 6 }}>
                                                                    <Typography variant="subtitle2" sx={{ 
                                                                        fontWeight: '600', 
                                                                        marginBottom: 1.5,
                                                                        color: '#667eea',
                                                                        fontSize: '11px',
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.5px'
                                                                    }}>
                                                                        Booking Details
                                                                    </Typography>
                                                                    <Table size="small" sx={{ 
                                                                        background: 'white',
                                                                        borderRadius: '8px',
                                                                        overflow: 'hidden',
                                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                                                    }}>
                                                                        <TableHead>
                                                                            <TableRow sx={{ background: '#f1f5f9' }}>
                                                                                <TableCell style={{ fontSize: '10px', fontWeight: '600', color: '#475569', padding: '6px 12px' }}>
                                                                                    #
                                                                                </TableCell>
                                                                                <TableCell style={{ fontSize: '10px', fontWeight: '600', color: '#475569', padding: '6px 12px' }}>
                                                                                    On Rent Qty
                                                                                </TableCell>
                                                                                <TableCell style={{ fontSize: '10px', fontWeight: '600', color: '#475569', padding: '6px 12px' }}>
                                                                                    Forecast Qty
                                                                                </TableCell>
                                                                                <TableCell style={{ fontSize: '10px', fontWeight: '600', color: '#475569', padding: '6px 12px' }}>
                                                                                    Forecast Date
                                                                                </TableCell>
                                                                                <TableCell style={{ fontSize: '10px', fontWeight: '600', color: '#475569', padding: '6px 12px' }}>
                                                                                    Expected Return
                                                                                </TableCell>
                                                                                <TableCell style={{ fontSize: '10px', fontWeight: '600', color: '#475569', padding: '6px 12px' }}>
                                                                                    Return Time
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {group.instances.map((instance, idx) => (
                                                                                <TableRow 
                                                                                    key={idx}
                                                                                    sx={{ 
                                                                                        '&:hover': { background: '#f8fafc' },
                                                                                        borderBottom: idx === group.instances.length - 1 ? 'none' : '1px solid #e2e8f0'
                                                                                    }}
                                                                                >
                                                                                    <TableCell style={{ fontSize: '11px', padding: '8px 12px', color: '#64748b', fontWeight: '500' }}>
                                                                                        {idx + 1}
                                                                                    </TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '8px 12px', color: '#f59e0b', fontWeight: '600' }}>
                                                                                        {instance.CurrentlyOnRentQty || 0}
                                                                                    </TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '8px 12px', color: '#667eea', fontWeight: '600' }}>
                                                                                        {instance.ForecastQty || 0}
                                                                                    </TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '8px 12px', color: '#2d3748' }}>
                                                                                        {(instance.ForecastDate)}
                                                                                    </TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '8px 12px', color: '#2d3748' }}>
                                                                                        {(instance.ExpectedReturnDate)}
                                                                                    </TableCell>
                                                                                    <TableCell style={{ fontSize: '11px', padding: '8px 12px', color: '#64748b' }}>
                                                                                        {instance.ExpectedReturnTime || 'N/A'}
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </Box>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>

                    {/* Pagination */}
                    <TablePagination
                        component="div"
                        count={filteredData.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        sx={{
                            '& .MuiTablePagination-toolbar': {
                                fontSize: '12px'
                            },
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontSize: '12px',
                                fontWeight: 600
                            }
                        }}
                    />
                </div>
            </div>
        </Fade>
    );
}

export default ItemForecastReport;
